import type { URL } from "@izicvs/schema";
import { urlSchema } from "@izicvs/schema";
import { Button, Input, Popover, PopoverContent, PopoverTrigger, Tooltip } from "@izicvs/ui";
import { normalizeUrl } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { Tag } from "@phosphor-icons/react";
import { forwardRef, useMemo } from "react";

type Props = {
  id?: string;
  value: URL;
  placeholder?: string;
  onChange: (value: URL) => void;
};

export const URLInput = forwardRef<HTMLInputElement, Props>(
  ({ id, value, placeholder, onChange }, ref) => {
    const normalizedUrl = normalizeUrl(value.href);
    const hasError = useMemo(
      () => !urlSchema.safeParse({ ...value, href: normalizedUrl }).success,
      [value, normalizedUrl],
    );

    // More informative error message
    const errorMessage = useMemo(() => {
      if (!hasError) return null;

      if (!value.href.trim()) {
        return t`Please enter a URL`;
      }

      return t`Invalid URL. Examples: google.com, www.example.com, https://site.com`;
    }, [hasError, value.href]);

    const handleInputChange = (inputValue: string) => {
      // Store the raw value while the user is typing
      onChange({ ...value, href: inputValue });
    };

    const handleInputBlur = () => {
      // Normalize the URL when the user leaves the field
      const normalized = normalizeUrl(value.href);
      if (normalized !== value.href) {
        onChange({ ...value, href: normalized });
      }
    };

    return (
      <>
        <div className="flex gap-x-1">
          <Input
            ref={ref}
            id={id}
            value={value.href}
            className="flex-1"
            hasError={hasError}
            placeholder={placeholder ?? t`example.com or https://site.com`}
            onChange={(event) => {
              handleInputChange(event.target.value);
            }}
            onBlur={handleInputBlur}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleInputBlur();
              }
            }}
          />

          <Popover>
            <Tooltip content={t`Label`}>
              <PopoverTrigger asChild>
                <Button size="icon" variant="ghost">
                  <Tag />
                </Button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent className="p-1.5">
              <Input
                value={value.label}
                placeholder={t`Label`}
                onChange={(event) => {
                  onChange({ ...value, label: event.target.value });
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        {hasError && <small className="text-red-500 opacity-75">{errorMessage}</small>}
      </>
    );
  },
);
